// Croqui: `components/Table.tsx` (Table + Col). Substitui `components/app/data-table.tsx`.
//
// O croqui é só a grade cheia; os estados (carregando, vazio, erro) e a paginação por
// cursor não existem lá e são obrigatórios aqui (`spec/17` §7 item 6, `spec/00` §4.6).
//
// Em 390px a tabela vira lista de cartões com os campos `compact` (`spec/24` §4 item 3):
// título da linha, valor à direita e o status. Marque no máximo três.

import type { ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import { ErrorState } from '../app/error-state.tsx';
import { TableSkeleton } from '../app/skeletons.tsx';
import { Btn } from './btn.tsx';
import { Empty } from './empty.tsx';
import type { IconName } from './icon.tsx';

/** Recuo da primeira e da última coluna: o respiro do `Panel`, com piso no `px-3` da célula. */
const EDGE =
  'first:pl-[max(var(--panel-pad,0px),0.75rem)] last:pr-[max(var(--panel-pad,0px),0.75rem)]';

export interface Col<T> {
  key: string;
  label: ReactNode;
  /** Largura da coluna (`'110px'`, `'20%'`). Sem ela, a coluna divide o que sobra. */
  w?: string;
  align?: 'left' | 'right';
  compact?: boolean;
  /**
   * Só existe na lista de 390px (não entra na tabela). Serve pra dar o NOME da linha ao
   * cartão quando a coluna do nome é um `RowOpenButton` (que não pode ser `compact`).
   */
  mobileOnly?: boolean;
  render: (row: T) => ReactNode;
}

export interface TableProps<T> {
  cols: Col<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Conveniência de MOUSE. O caminho de teclado é o `RowOpenButton` numa célula. */
  onRow?: (row: T) => void;
  selected?: (row: T) => boolean;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** Paginação por cursor (`spec/00` §4.6): `next_cursor` presente = tem mais. */
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  emptyTitle?: string;
  emptyHint?: string;
  emptyAction?: ReactNode;
  /** Ícone do vazio. Sem ele, `inbox` (o croqui usa `check` em fila zerada). */
  emptyIcon?: IconName;
}

/**
 * Caminho de TECLADO da linha: `<tr onClick>` não recebe Tab, e marcar a `<tr>` como
 * `role="button"` colocaria um controle dentro do outro (`nested-interactive`) nas telas
 * que já põem um botão numa célula.
 *
 * Use numa coluna que NÃO seja `compact`: as `compact` viram o conteúdo do `<button>` do
 * cartão de 390px e botão dentro de botão é HTML inválido.
 */
export function RowOpenButton({
  label,
  onOpen,
  children,
}: {
  /** Vai para o `aria-label`: "Abrir <label>". */
  label: string;
  onOpen: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={`Abrir ${label}`}
      onClick={(event) => {
        // Sem isto o clique sobe para a linha e o detalhe abriria duas vezes.
        event.stopPropagation();
        onOpen();
      }}
      className="min-w-0 rounded-md text-left underline-offset-4 hover:underline"
    >
      {children}
    </button>
  );
}

export function Table<T>({
  cols,
  rows,
  rowKey,
  onRow,
  selected,
  isLoading = false,
  error,
  onRetry,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  emptyTitle = 'Nada por aqui ainda',
  emptyHint,
  emptyAction,
  emptyIcon,
}: TableProps<T>) {
  if (error !== undefined && error !== null) {
    return <ErrorState error={error} {...(onRetry === undefined ? {} : { onRetry })} />;
  }
  if (isLoading) {
    return <TableSkeleton columns={cols.length} />;
  }
  if (rows.length === 0) {
    return (
      <Empty
        title={emptyTitle}
        {...(emptyIcon === undefined ? {} : { icon: emptyIcon })}
        {...(emptyHint === undefined ? {} : { hint: emptyHint })}
        {...(emptyAction === undefined ? {} : { action: emptyAction })}
      />
    );
  }

  const marked = cols.filter((col) => col.compact === true);
  const cardCols = marked.length > 0 ? marked : cols.slice(0, 3);
  const deskCols = cols.filter((col) => col.mobileOnly !== true);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* `relative w-full min-w-0` no invólucro que rola, os três:
          - `w-full`/`min-w-0` prendem a largura à do painel;
          - `relative` é o que faz dele o BLOCO CONTENDOR dos filhos absolutos. `overflow`
            clipa descendente NO FLUXO; descendente absoluto só é clipado se o bloco
            contendor for o próprio container. Sem `relative`, um `sr-only` (1x1,
            `position: absolute`) de uma célula se posiciona contra o viewport, escapa do
            `overflow-x-auto` e empurra a PÁGINA: medido em `admin-sellers.spec.ts` a
            1024px, `html.scrollWidth` 1088 com `body` em 1024 (`spec/17` §7 item 11). */}
      {/* WEB-133, o mesmo desenho de `components/ui/table.tsx`: cabeçalho em `micro-label`,
          linha separada por um fio, hover e seleção na célula do sistema. Dentro de um
          `Panel` a tabela SANGRA até a borda do cartão (`--panel-pad` é o respiro dele) e
          a primeira e a última coluna recuam o mesmo tanto, como na lista de transações do
          lojista. Fora de `Panel` a variável vale 0 e nada muda. */}
      <div className="relative mx-[calc(var(--panel-pad,0px)*-1)] hidden w-[calc(100%+var(--panel-pad,0px)*2)] min-w-0 overflow-x-auto sm:block">
        {/* `table-fixed` só quando alguém pediu largura: com `auto` o navegador ignora o
            `width` do `<th>` sempre que o conteúdo pede mais, e o `Col.w` vira enfeite. */}
        <table
          className={cn(
            // 13px (o `ui/table` é 14): as tabelas do admin têm mais colunas e a 1024px
            // um ponto a mais já põe várias delas pra rolar de lado.
            'w-full caption-bottom border-collapse text-13',
            deskCols.some((col) => col.w !== undefined) && 'table-fixed',
          )}
        >
          <thead>
            <tr className="border-border border-b">
              {deskCols.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={cn(
                    'micro-label h-10 px-3 align-middle whitespace-nowrap',
                    EDGE,
                    // `<th>` nasce `text-align: center` do navegador e o preflight do
                    // Tailwind v4 não o reseta. O croqui alinha todo cabeçalho à coluna.
                    col.align === 'right' ? 'text-right' : 'text-left',
                  )}
                  style={col.w === undefined ? undefined : { width: col.w }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                className={cn(
                  'border-border border-b tabular-nums transition-colors duration-150 last:border-0',
                  selected?.(row) === true && 'bg-secondary',
                  onRow !== undefined && 'cursor-pointer hover:bg-secondary/60',
                )}
                {...(onRow === undefined
                  ? {}
                  : {
                      onClick: () => {
                        onRow(row);
                      },
                    })}
              >
                {deskCols.map((col) => (
                  <td
                    key={col.key}
                    className={cn(
                      'px-3 py-2.5 align-middle',
                      EDGE,
                      col.align === 'right' && 'text-right',
                    )}
                  >
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 390px: lista de linhas separadas por um fio (o `ListRow` do lojista), não uma
          pilha de cartões com borda. Sangra até a borda do `Panel`, como a tabela. */}
      <ul className="mx-[calc(var(--panel-pad,0px)*-1)] flex flex-col sm:hidden">
        {rows.map((row) => (
          <li key={rowKey(row)} className="border-border border-t first:border-t-0">
            <button
              type="button"
              disabled={onRow === undefined}
              onClick={() => {
                onRow?.(row);
              }}
              className="flex min-h-11 w-full flex-col gap-1.5 px-[max(var(--panel-pad,0px),0.75rem)] py-3.5 text-left transition-colors duration-150 enabled:hover:bg-secondary/60 focus-visible:-outline-offset-2 disabled:cursor-default"
            >
              {cardCols.map((col) =>
                col.mobileOnly === true ? (
                  // O nome da linha: título do cartão, sem rótulo.
                  <span key={col.key} className="text-15 font-medium">
                    {col.render(row)}
                  </span>
                ) : (
                  <span key={col.key} className="flex items-baseline justify-between gap-3">
                    {/* `span`, não `Eyebrow`: um `div` dentro de `span` é HTML inválido. */}
                    <span className="whitespace-nowrap font-mono font-medium text-10 text-muted-foreground uppercase tracking-[0.08em]">
                      {col.label}
                    </span>
                    <span className="text-14">{col.render(row)}</span>
                  </span>
                ),
              )}
            </button>
          </li>
        ))}
      </ul>

      {hasMore && onLoadMore !== undefined && (
        <Btn
          kind="card"
          small
          onClick={onLoadMore}
          disabled={isLoadingMore}
          className="self-center"
        >
          {isLoadingMore ? 'Carregando…' : 'Carregar mais'}
        </Btn>
      )}
    </div>
  );
}
